import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Users2, Plus, Trash2, Copy, Send, Loader2, Instagram, Mail,
  MessageCircle, Youtube, Music2, Twitter, Linkedin, Globe,
  RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CANAUX = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-400' },
  { value: 'email',     label: 'Email',     icon: Mail,       color: 'text-blue-400' },
  { value: 'whatsapp',  label: 'WhatsApp',  icon: MessageCircle, color: 'text-emerald-400' },
  { value: 'tiktok',    label: 'TikTok',    icon: Music2,     color: 'text-cyan-400' },
  { value: 'youtube',   label: 'YouTube',   icon: Youtube,    color: 'text-red-400' },
  { value: 'twitter',   label: 'Twitter/X', icon: Twitter,    color: 'text-slate-300' },
  { value: 'linkedin',  label: 'LinkedIn',  icon: Linkedin,   color: 'text-blue-500' },
  { value: 'other',     label: 'Autre',     icon: Globe,      color: 'text-muted-foreground' },
];

const STATUSES = [
  { value: 'to_contact', label: 'À contacter',  color: 'bg-slate-500/20 text-slate-300' },
  { value: 'contacted',  label: 'Contacté',     color: 'bg-amber-500/20 text-amber-300' },
  { value: 'replied',    label: 'A répondu',    color: 'bg-blue-500/20 text-blue-300' },
  { value: 'interested', label: 'Intéressé',    color: 'bg-emerald-500/20 text-emerald-300' },
  { value: 'booked',     label: 'RDV pris',     color: 'bg-green-500/20 text-green-300' },
  { value: 'rejected',   label: 'Refus',        color: 'bg-rose-500/20 text-rose-300' },
];

// Version A — ultra-short Instagram DM template. Single message, fits without "See more".
export const INSTA_DM_TEMPLATE_A = `Salut [Prénom] 👋

Je suis Jérémy, fondateur de TRADEX AI — un copilote d'analyse graphique piloté par IA (ICT, multi-TF, Money Management strict). Ta façon d'expliquer le trading colle exactement à notre audience.

J'ai un cadeau pour toi : 7 jours d'accès Expert offerts, sans CB → code LAUNCH sur tradex-ai.fr

On a aussi un programme de redevance partenaires pour les créateurs qui en parlent. Ça t'intéresse d'en discuter ?`;

const canalMeta = (value) => CANAUX.find((c) => c.value === value) || CANAUX[CANAUX.length - 1];
const statusMeta = (value) => STATUSES.find((s) => s.value === value) || STATUSES[0];

const emptyForm = {
  name: '',
  canal: 'instagram',
  handle: '',
  email: '',
  reach: '',
  niche: '',
  notes: '',
};

export default function InfluencerProspectsCRM() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/influencer-prospects`);
      setProspects(res.data.prospects || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement des prospects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const resetForm = () => {
    setForm(emptyForm);
    setShowForm(false);
  };

  const addProspect = async () => {
    if (!form.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        canal: form.canal,
        handle: form.handle.trim() || undefined,
        email: form.email.trim() || undefined,
        reach: form.reach ? parseInt(form.reach, 10) : undefined,
        niche: form.niche.trim() || undefined,
        notes: form.notes.trim() || undefined,
        status: 'to_contact',
      };
      await axios.post(`${API}/admin/influencer-prospects`, payload);
      toast.success(`${form.name} ajouté(e) au CRM ✅`);
      resetForm();
      fetchProspects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur d\'ajout');
    } finally {
      setSaving(false);
    }
  };

  const updateProspect = async (id, updates, silent = false) => {
    try {
      await axios.patch(`${API}/admin/influencer-prospects/${id}`, updates);
      if (!silent) toast.success('Mise à jour ✅');
      // Optimistic local update to avoid extra fetch
      setProspects((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de mise à jour');
    }
  };

  const deleteProspect = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} du CRM ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/influencer-prospects/${id}`);
      toast.success('Prospect supprimé');
      setProspects((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de suppression');
    }
  };

  const copyInstaDM = async (prospect) => {
    const firstName = (prospect.name || '').split(' ')[0] || 'toi';
    const dm = INSTA_DM_TEMPLATE_A.replaceAll('[Prénom]', firstName);
    try {
      await navigator.clipboard.writeText(dm);
      toast.success(`DM Instagram pour ${firstName} copié dans le presse-papier ✅`);
      // Auto-mark as contacted + log timestamp + template
      await updateProspect(prospect.id, {
        status: prospect.status === 'to_contact' ? 'contacted' : prospect.status,
        template_used: 'insta_dm_v1',
        last_contacted_at: new Date().toISOString(),
      }, true);
    } catch (err) {
      toast.error('Copie impossible — sélectionnez le texte manuellement');
    }
  };

  const sendEmailToProspect = async (prospect) => {
    if (!prospect.email) {
      toast.error('Aucune adresse email pour ce prospect');
      return;
    }
    const firstName = (prospect.name || '').split(' ')[0] || undefined;
    try {
      const res = await axios.post(`${API}/admin/mail-templates/influencer/send`, {
        to: prospect.email,
        first_name: firstName,
      });
      toast.success(`Email envoyé à ${res.data.to} ✅`);
      await updateProspect(prospect.id, {
        status: prospect.status === 'to_contact' ? 'contacted' : prospect.status,
        template_used: 'email_v1',
        last_contacted_at: new Date().toISOString(),
      }, true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur d\'envoi email');
    }
  };

  const totalByStatus = STATUSES.reduce((acc, s) => {
    acc[s.value] = prospects.filter((p) => p.status === s.value).length;
    return acc;
  }, {});

  return (
    <Card className="p-5" data-testid="influencer-prospects-crm">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Users2 className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              CRM Prospects Influenceurs
              <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30 text-[10px]">
                {prospects.length} contacts
              </Badge>
            </h2>
            <p className="text-xs text-muted-foreground">
              Track chaque prospect par canal (Instagram, Email, WhatsApp…) et copie le bon message en 1 clic.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchProspects}
            disabled={loading}
            data-testid="prospects-reload-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Actualiser
          </Button>
          <Button
            size="sm"
            onClick={() => setShowForm((v) => !v)}
            className="bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="prospect-add-toggle-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {showForm ? 'Annuler' : 'Ajouter un prospect'}
          </Button>
        </div>
      </div>

      {/* Status counters */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
        {STATUSES.map((s) => (
          <div
            key={s.value}
            className={`px-2 py-2 rounded-lg text-center border border-white/5 ${s.color.replace('text-', 'bg-').replace('/20', '/5')}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className="text-lg font-bold font-mono">{totalByStatus[s.value] || 0}</div>
          </div>
        ))}
      </div>

      {/* Add form (collapsible) */}
      {showForm && (
        <div className="mb-5 p-4 rounded-lg border border-purple-500/25 bg-purple-500/5" data-testid="prospect-add-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <Label className="text-xs mb-1 block">Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Jean Dupont"
                data-testid="prospect-name-input"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Canal *</Label>
              <Select value={form.canal} onValueChange={(v) => setForm((f) => ({ ...f, canal: v }))}>
                <SelectTrigger data-testid="prospect-canal-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANAUX.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Handle / URL</Label>
              <Input
                value={form.handle}
                onChange={(e) => setForm((f) => ({ ...f, handle: e.target.value }))}
                placeholder="@handle_insta ou lien profil"
                data-testid="prospect-handle-input"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Email (pour envoi direct)</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="pro@example.com"
                data-testid="prospect-email-input"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Audience (nb followers)</Label>
              <Input
                type="number"
                min="0"
                value={form.reach}
                onChange={(e) => setForm((f) => ({ ...f, reach: e.target.value }))}
                placeholder="50000"
                data-testid="prospect-reach-input"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Niche</Label>
              <Input
                value={form.niche}
                onChange={(e) => setForm((f) => ({ ...f, niche: e.target.value }))}
                placeholder="Crypto, ICT, Forex, Macro…"
                data-testid="prospect-niche-input"
              />
            </div>
          </div>
          <div className="mb-3">
            <Label className="text-xs mb-1 block">Notes (option)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Contexte, contenus qu'il/elle produit, best-time…"
              data-testid="prospect-notes-input"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={resetForm} disabled={saving}>Annuler</Button>
            <Button
              size="sm"
              onClick={addProspect}
              disabled={saving || !form.name.trim()}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="prospect-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Sauvegarder
            </Button>
          </div>
        </div>
      )}

      {/* Prospects table */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Chargement…
        </div>
      ) : prospects.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-sm border border-dashed border-white/10 rounded-lg">
          Aucun prospect pour l'instant. Clique <strong>Ajouter un prospect</strong> pour démarrer.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/10">
                <th className="px-3 py-2 font-medium">Nom</th>
                <th className="px-3 py-2 font-medium">Canal</th>
                <th className="px-3 py-2 font-medium">Handle / Email</th>
                <th className="px-3 py-2 font-medium">Reach</th>
                <th className="px-3 py-2 font-medium">Niche</th>
                <th className="px-3 py-2 font-medium">Statut</th>
                <th className="px-3 py-2 font-medium">Dernier contact</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {prospects.map((p) => {
                const cm = canalMeta(p.canal);
                const CanalIcon = cm.icon;
                const isInsta = p.canal === 'instagram' || p.canal === 'tiktok' || p.canal === 'twitter';
                return (
                  <tr
                    key={p.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`prospect-row-${p.id}`}
                  >
                    <td className="px-3 py-3 font-medium">{p.name}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${cm.color}`}>
                        <CanalIcon className="w-3.5 h-3.5" />
                        {cm.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground max-w-[220px] truncate">
                      {p.handle && <div className="truncate">{p.handle}</div>}
                      {p.email && <div className="truncate text-blue-300">{p.email}</div>}
                      {!p.handle && !p.email && <span className="text-muted-foreground/50">—</span>}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {p.reach ? p.reach.toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs">{p.niche || '—'}</td>
                    <td className="px-3 py-3">
                      <Select
                        value={p.status}
                        onValueChange={(v) => updateProspect(p.id, { status: v })}
                      >
                        <SelectTrigger className="h-7 text-xs w-[130px]" data-testid={`prospect-status-${p.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      {p.last_contacted_at
                        ? new Date(p.last_contacted_at).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {isInsta && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyInstaDM(p)}
                            title="Copier le DM Instagram (Version A)"
                            data-testid={`prospect-copy-dm-${p.id}`}
                          >
                            <Copy className="w-3.5 h-3.5 text-pink-400" />
                          </Button>
                        )}
                        {p.canal === 'email' && p.email && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => sendEmailToProspect(p)}
                            title="Envoyer le mail via Resend"
                            data-testid={`prospect-send-email-${p.id}`}
                          >
                            <Send className="w-3.5 h-3.5 text-blue-400" />
                          </Button>
                        )}
                        {!isInsta && p.canal !== 'email' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyInstaDM(p)}
                            title="Copier le message court (adaptable)"
                            data-testid={`prospect-copy-msg-${p.id}`}
                          >
                            <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteProspect(p.id, p.name)}
                          title="Supprimer"
                          data-testid={`prospect-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
